import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
//third party
import {
  catchError,
  firstValueFrom,
  lastValueFrom,
  map,
  throwError,
} from 'rxjs';
import {
  CSSISearchTypes,
  CSSI_Response,
  CSSI_Error_Response,
} from '../models/cssi';
import {
  DistributorConfig,
  DistributorInventoryProfile,
} from '../models/general';
import { SharedService } from './shared.service';
import { DistributorList } from '../models/constants';

@Injectable({
  providedIn: 'root',
})
export class DistributorService {
  apiUrl: string = 'http://localhost:3000/api'; // may want to convert to sharedService variable
  distributor_configs: DistributorConfig[] = [];
  live_inv_selected_distributors: DistributorInventoryProfile[] = [];
  constructor(private http: HttpClient, private sharedService: SharedService) {}

  async CSSI_GetItem(
    item_number: string,
    search_type: string
  ): Promise<CSSI_Response> {
    const url = `${this.apiUrl}/cssi`;
    let params = new HttpParams();
    const distributor: { name: string; value: string } = DistributorList.filter(
      (distributor) => distributor.name === 'cssi'
    )[0];
    if (
      this.distributor_configs.filter(
        (config) => config.distributor_name === distributor.value
      )[0] === undefined
    ) {
      console.error(`No ${distributor.value} distributor config found`);
      throw new Error(`No ${distributor.value} distributor config found`);
    } else {
      const sid = this.distributor_configs.filter(
        (config) => config.distributor_name === distributor.value
      )[0].sid;
      const token = this.distributor_configs.filter(
        (config) => config.distributor_name === distributor.value
      )[0].token;
      // Add search_type and item_number to query parameters
      if (search_type === CSSISearchTypes.upc) {
        params = params.append(CSSISearchTypes.upc, item_number);
      } else if (search_type === CSSISearchTypes.ccsi_id) {
        params = params.append(CSSISearchTypes.ccsi_id, item_number);
      } else {
        params = params.append(CSSISearchTypes.manufacturer_id, item_number);
      }
      // Set sid and token in headers
      const headers = new HttpHeaders().set('sid', sid).set('token', token);

      try {
        const response = await firstValueFrom(
          this.http.get<CSSI_Response>(url, { params, headers }).pipe(
            map((response: CSSI_Response) => response),
            catchError((error) => {
              console.error('Full error response:', error);
              if (error.error?.errors?.length > 0) {
                console.error('Error message:', error.error.errors[0].message);
              }
              return throwError(() => error);
            })
          )
        );
        return response;
      } catch (error) {
        console.error('Error occurred:', error);
        throw error;
      }
    }
  }

  //-----------Distributor Config CRUD Functions MongoDB-----------------
  async saveDistributorConfig(
    distributor_config: DistributorConfig
  ): Promise<{ message: string; configs: string[] }> {
    const url = `${this.apiUrl}/distributor_configs`;
    const response = await lastValueFrom(
      this.http.post<{ message: string; configs: string[] }>(
        url,
        distributor_config
      )
    );
    // Refresh local configs and sync with sharedService
    await this.getAllDistributorConfigs();
    return response;
  }
  async updateDistributorConfig(
    id: string,
    distributorConfig: DistributorConfig,
  ): Promise<{ message: string; config: DistributorConfig }> {
    try {
      const url = `${this.apiUrl}/distributor_configs/${id}`;
      const response = await lastValueFrom(
        this.http.put<{ message: string; config: DistributorConfig }>(
          url,
          distributorConfig,
        ),
      );
      await this.getAllDistributorConfigs();
      return response;
    } catch (error) {
      console.error('Error updating distributor config:', error);
      this.sharedService.raiseErrorToast('Failed to update distributor config');
      throw error; // Re-throw to let the caller handle it if needed
    }
  }
  async getAllDistributorConfigs(): Promise<DistributorConfig[]> {
    try {
      const url = `${this.apiUrl}/distributor_configs`;
      const configs = await lastValueFrom(
        this.http.get<DistributorConfig[]>(url)
      );
      this.distributor_configs = configs;
      if (this.sharedService.company_info) {
        this.sharedService.company_info.distributor_configs = configs;
      }
      return configs;
    } catch (error) {
      console.error('Error fetching distributor configs:', error);
      throw error; // Re-throw to let the caller handle it if needed
    }
  }
  async deleteDistributorConfig(
    id: string
  ): Promise<{ message: string; config: DistributorConfig }> {
    try {
      const url = `${this.apiUrl}/distributor_configs/${id}`;
      const response = await lastValueFrom(
        this.http.delete<{ message: string; config: DistributorConfig }>(url)
      );
      // Refresh local configs and sync with sharedService
      await this.getAllDistributorConfigs();
      return response;
    } catch (error) {
      console.error('Error deleting distributor config:', error);
      throw error; // Re-throw to let the caller handle it if needed
    }
  }

  //-----------Distributor Config CRUD Functions MongoDB-----------------
  async saveLiveInventoryDistributorList(): Promise<{
    message: string;
    configs: string[];
  }> {
    try {
      const url = `${this.apiUrl}/live_inventory_distributor_selections`;
      const response = await lastValueFrom(
        this.http.post<{ message: string; configs: string[] }>(
          url,
          this.live_inv_selected_distributors
        )
      );
      return response;
    } catch (error) {
      console.error('Error fetching distributor configs:', error);
      throw error; // Re-throw to let the caller handle it if needed
    }
  }
  async getLiveInventoryDistributorList(): Promise<DistributorInventoryProfile[]> {
    if (this.sharedService.company_info && this.sharedService.company_info.live_inv_selected_distributors) {
      this.live_inv_selected_distributors = this.sharedService.company_info.live_inv_selected_distributors;
    } else {
      this.live_inv_selected_distributors = [];
    }
    return this.live_inv_selected_distributors;
  }
  async updateLiveInventoryDistributorList(): Promise<DistributorInventoryProfile[]> {
    try {
      const url = `${this.apiUrl}/live_inventory_distributor_selections/`;
      const response = await lastValueFrom(
        this.http.put<DistributorInventoryProfile[]>(
          url,
          this.live_inv_selected_distributors
        )
      );
      if (this.sharedService.company_info) {
        this.sharedService.company_info.live_inv_selected_distributors = this.live_inv_selected_distributors;
      }
      return response;
    } catch (error) {
      console.error('Error updating distributor config:', error);
      this.sharedService.raiseErrorToast('Failed to update distributor config');
      throw error; // Re-throw to let the caller handle it if needed
    }
  }
  
}
